/**
 * Standardized Entity Reducers
 * Implements Smart Data-Flow Architecture principles
 * Provides consistent state management for all entities
 * 
 * Eliminates inconsistent state update patterns found in audit:
 * - Different update logic per component
 * - Inconsistent error handling 
 * - Scattered state management across 103+ components
 */

// Import actions from actions.js for store integration  
import {
  staffActions,
  corporateActions,
  driverActions,
  routeActions,
  tripActions,
  bookingActions,
  departmentActions,
  settingsActions,
  authActions,
  uiActions,
  compositeActions
} from './actions';

// Development logging using standard NODE_ENV

/**
 * Generic entity reducer factory
 * Creates consistent reducers for any entity type
 */
export const createEntityReducer = (entityType) => {
  const ENTITY_UPPER = entityType.toUpperCase();
  
  const initialState = {
    data: [],
    loading: false,
    error: null,
    lastUpdated: null,
    filters: {},
    selectedItems: [],
    pagination: {
      page: 0,
      rowsPerPage: 10,
      total: 0
    }
  };

  return (state = initialState, action) => {
    // Only handle actions for this entity type
    if (!action.type.startsWith(ENTITY_UPPER)) return state;

    const actionType = action.type.split('/')[1];

    switch (actionType) {
      case 'SET':
        return {
          ...state,
          data: Array.isArray(action.payload) ? action.payload : [],
          loading: false,
          error: null,
          lastUpdated: action.timestamp
        };

      case 'ADD':
        return {
          ...state,
          data: [...state.data, action.payload],
          lastUpdated: action.timestamp
        };

      case 'UPDATE':
        const { id, updates } = action.payload;
        return {
          ...state,
          data: state.data.map(item => {
            // Handle different ID field names (StaffId, StaffID, DriverID, etc.)
            const itemId = item.id || item[`${entityType}ID`] || item[`${entityType}Id`];
            return itemId === id ? { ...item, ...updates } : item;
          }),
          lastUpdated: action.timestamp
        };

      case 'REMOVE':
        return {
          ...state,
          data: state.data.filter(item => {
            const itemId = item.id || item[`${entityType}ID`] || item[`${entityType}Id`];
            return itemId !== action.payload;
          }),
          lastUpdated: action.timestamp
        };

      case 'SET_LOADING':
        return {
          ...state,
          loading: action.payload
        };

      case 'SET_ERROR':
        return {
          ...state,
          error: action.payload,
          loading: false
        };

      case 'CLEAR_ERROR':
        return {
          ...state,
          error: null
        };

      case 'CLEAR':
        return {
          ...initialState,
          lastUpdated: action.timestamp
        };

      case 'SET_FILTERS':
        return {
          ...state,
          filters: { ...state.filters, ...action.payload }
        };

      case 'SET_SELECTED':
        return {
          ...state,
          selectedItems: Array.isArray(action.payload) ? action.payload : []
        };

      case 'BATCH_UPDATE':
        return {
          ...state,
          data: state.data.map(item => {
            const update = action.payload.find(u => {
              const itemId = item.id || item[`${entityType}ID`] || item[`${entityType}Id`];
              return itemId === u.id;
            });
            return update ? { ...item, ...update.data } : item;
          }),
          lastUpdated: action.timestamp
        };

      case 'REFRESH':
        // Trigger for re-fetching data (handled by middleware)
        return {
          ...state,
          lastUpdated: action.timestamp
        };

      case 'SET_SUCCESS':
        return {
          ...state,
          error: null,
          loading: false,
          lastUpdated: action.timestamp
        };

      default:
        return state;
    }
  };
};

/**
 * Pre-created reducers for all entities
 * Consistent state management across the entire application
 */
const staffReducer = createEntityReducer('STAFF');
const corporateReducer = createEntityReducer('CORPORATE');
const driverReducer = createEntityReducer('DRIVER');
const routeReducer = createEntityReducer('ROUTE');
const tripReducer = createEntityReducer('TRIP');
const bookingReducer = createEntityReducer('BOOKING');
const departmentReducer = createEntityReducer('DEPARTMENT');
const settingsReducer = createEntityReducer('SETTINGS');
const authReducer = createEntityReducer('AUTH');

/**
 * UI Reducer for global application state
 */
const uiInitialState = {
  globalLoading: false,
  globalError: null,
  notifications: [],
  currentRoute: null,
  globalFilters: {},
  theme: 'light',
  sidebarOpen: true
};

const uiReducer = (state = uiInitialState, action) => {
  switch (action.type) {
    case 'UI/SET_GLOBAL_LOADING':
      return { ...state, globalLoading: action.payload };
      
    case 'UI/SET_GLOBAL_ERROR':
      return { ...state, globalError: action.payload };
      
    case 'UI/CLEAR_GLOBAL_ERROR':
      return { ...state, globalError: null };
      
    case 'UI/SET_NOTIFICATION':
      return { 
        ...state, 
        notifications: [...state.notifications, action.payload]
      };
      
    case 'UI/REMOVE_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.filter(n => n.id !== action.payload)
      };
      
    case 'UI/SET_CURRENT_ROUTE':
      return { ...state, currentRoute: action.payload };
      
    case 'UI/SET_GLOBAL_FILTERS':
      return { ...state, globalFilters: { ...state.globalFilters, ...action.payload } };
      
    default:
      return state;
  }
};

/**
 * Composite Reducer for handling complex operations
 */
const compositeReducer = (state = {}, action) => {
  switch (action.type) {
    case 'COMPOSITE/LOAD_ALL_DATA':
      // Trigger loading state for all entities
      return {
        ...state,
        staff: { ...state.staff, loading: true },
        corporates: { ...state.corporates, loading: true },
        drivers: { ...state.drivers, loading: true },
        routes: { ...state.routes, loading: true }
      };
      
    case 'COMPOSITE/CLEAR_ALL_DATA':
      // Reset all entity data (for logout)
      return {
        staff: staffReducer(undefined, { type: 'STAFF/CLEAR' }),
        corporates: corporateReducer(undefined, { type: 'CORPORATE/CLEAR' }),
        drivers: driverReducer(undefined, { type: 'DRIVER/CLEAR' }),
        routes: routeReducer(undefined, { type: 'ROUTE/CLEAR' }),
        trips: tripReducer(undefined, { type: 'TRIP/CLEAR' }),
        bookings: bookingReducer(undefined, { type: 'BOOKING/CLEAR' }),
        departments: departmentReducer(undefined, { type: 'DEPARTMENT/CLEAR' }),
        ui: uiReducer(undefined, { type: 'UI/CLEAR' })
      };
      
    case 'BATCH_ACTION':
      // Handle multiple actions at once
      let newState = state;
      action.payload.forEach(subAction => {
        newState = rootReducer(newState, subAction);
      });
      return newState;
      
    default:
      return state;
  }
};

/**
 * Root reducer combining all entity reducers
 * Smart Data-Flow: Single source of truth for all application state
 */
const rootReducer = (state = {}, action) => {
  // Handle composite actions first
  if (action.type.startsWith('COMPOSITE/') || action.type === 'BATCH_ACTION') {
    return compositeReducer(state, action);
  }
  
  return {
    // Entity state
    staff: staffReducer(state.staff, action),
    corporates: corporateReducer(state.corporates, action),
    drivers: driverReducer(state.drivers, action),
    routes: routeReducer(state.routes, action),
    trips: tripReducer(state.trips, action),
    bookings: bookingReducer(state.bookings, action),
    departments: departmentReducer(state.departments, action),
    settings: settingsReducer(state.settings, action),
    auth: authReducer(state.auth, action),
    
    // UI state
    ui: uiReducer(state.ui, action)
  };
};

/**
 * Smart Data-Flow: Enhanced AppStore integration
 * Extends the existing AppStore to use standardized actions
 */
const createEnhancedStore = (existingStore) => {
  const originalUpdateState = existingStore.updateState.bind(existingStore);
  
  // Add dispatch method to existing store
  existingStore.dispatch = (action) => {
    console.log('📦 [ENHANCED_STORE] Dispatching action:', action.type);
    
    const newState = rootReducer(existingStore.state, action);
    existingStore.state = newState;
    existingStore.notifyListeners();
    
      if (process.env.NODE_ENV === 'development') {
        console.log('📦 [ENHANCED_STORE] State updated:', {
          action: action.type,
          timestamp: action.timestamp
        });
      }
  };
  
  // Add action creators to store for easy access
  existingStore.actions = {
    staff: staffActions,
    corporates: corporateActions,
    drivers: driverActions,
    routes: routeActions,
    trips: tripActions,
    bookings: bookingActions,
    departments: departmentActions,
    settings: settingsActions,
    ui: uiActions,
    composite: compositeActions
  };
  
  return existingStore;
};

// Export individual items for granular imports
// Note: createEntityReducer is already exported above as a function
export {
  // Reducers
  staffReducer,
  corporateReducer,
  driverReducer,
  routeReducer,
  tripReducer,
  bookingReducer,
  departmentReducer,
  settingsReducer,
  authReducer,
  uiReducer,
  compositeReducer,
  rootReducer,
  
  // Enhanced store function
  createEnhancedStore
};

export default rootReducer;
